// ─────────────────────────────────────────────────────────────────────────────
// MatchScoreBadge.jsx  —  new file, place in src/common/MatchScoreBadge.jsx
// Used by HomeCard (card badge) and Details (detail page banner)
// ─────────────────────────────────────────────────────────────────────────────
import React from "react";
import { getScoreColor, getScoreLabel } from "../utils/buyerMatchScore";

// ── Small circular badge for HomeCard ────────────────────────────────────────
export function MatchScoreBadge({ score, hasProfile, isLoggedIn, onClick }) {
  if (!isLoggedIn) {
    return (
      <div style={{
        display: "inline-block", padding: "4px 10px",
        background: "#f0f2ff", borderRadius: "50px",
        border: "2px solid #D4922A",
        animation: "pulse 2s infinite",
        fontSize: "11px", color: "#2A3390", fontWeight: 600,
        cursor: "pointer", marginTop: "6px",
      }} onClick={onClick}>
        "⭐ Get match scores for all listings — create your profile"
      </div>
    );
  }

  if (!hasProfile) {
    return (
      <div style={{
        display: "inline-block", padding: "4px 10px",
        background: "#f0f2ff", borderRadius: "50px",
        border: "1px dashed #2A3390",
        fontSize: "11px", color: "#2A3390", fontWeight: 600,
        cursor: "pointer", marginTop: "6px",
      }} onClick={onClick}>
        "⭐ Get match scores for all listings — create your profile"
      </div>
    );
  }

  if (score === null || score === undefined) {
    return (
      <div style={{
        display: "inline-flex", alignItems: "center", gap: "6px",
        padding: "4px 12px", background: "#f4f5fa", borderRadius: "50px",
        border: "1px solid #d4d8f1", fontSize: "11px", color: "#666",
        fontWeight: 600, marginTop: "6px", cursor: "pointer",
      }} onClick={onClick}>
        <span>Match Score:</span>
        <span style={{ color: "#2A3390", fontWeight: 800 }}>?</span>
        <span style={{ fontSize: "10px", color: "#2A3390" }}>Click to see ›</span>
      </div>
    );
  }

  const color = getScoreColor(score);
  const label = getScoreLabel(score);
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: "8px",
      padding: "4px 12px", background: color + "18", borderRadius: "50px",
      border: `1.5px solid ${color}`, marginTop: "6px",
    }}>
      <div style={{
        width: "28px", height: "28px", borderRadius: "50%",
        background: color, display: "flex", alignItems: "center",
        justifyContent: "center", flexShrink: 0,
      }}>
        <span style={{ color: "white", fontSize: "10px", fontWeight: 800 }}>{score}</span>
      </div>
      <div>
        <div style={{ fontSize: "11px", fontWeight: 800, color }}>{label}</div>
        <div style={{ fontSize: "10px", color: "#666" }}>House match</div>
      </div>
    </div>
  );
}

// ── Expanded banner for Details page ─────────────────────────────────────────
export function MatchScoreBanner({
  score, tier1, tier2, breakdown, tier2Breakdown,
  hasLifeAddresses, onGetAIAnalysis, aiLoading, aiResult, onAddLifeAddresses,
}) {
  if (score === null || score === undefined) return null;
  const color = getScoreColor(score);
  const label = getScoreLabel(score);

  return (
    <div style={{
      background: "white", border: `2px solid ${color}`,
      borderRadius: "12px", padding: "18px 22px", marginBottom: "16px",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "14px", flexWrap: "wrap" }}>
        <div style={{
          width: "56px", height: "56px", borderRadius: "50%",
          background: color, display: "flex", alignItems: "center",
          justifyContent: "center", flexShrink: 0,
        }}>
          <span style={{ color: "white", fontSize: "18px", fontWeight: 900 }}>{score}</span>
        </div>
        <div>
          <div style={{ fontSize: "18px", fontWeight: 800, color }}>{label}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {score}/90 pts · House criteria: {tier1}/80 pts
            {tier2 > 0 && ` · Life addresses: ${tier2}/20 pts`}
          </div>
        </div>
        <div
          onClick={onAddLifeAddresses}
          style={{
            marginLeft: "auto", padding: "6px 14px",
            background: "#FFF8E6", borderRadius: "50px",
            border: "1.5px solid #D4921A", fontSize: "11px",
            color: "#92400E", fontWeight: 700,
            cursor: "pointer",
          }}>
          {hasLifeAddresses ? "✏️ Edit important addresses" : "+ Add important addresses to enhance scoring"}
        </div>
      </div>

      {/* Tier 1 breakdown */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px" }}>
        {breakdown.filter(b => b.label !== "Life Address Match").map((item, i) => (
          <div key={i} style={{
            flex: "1 1 140px", background: item.score >= item.max * 0.7 ? "#f0fdf4" : item.score > 0 ? "#fffbeb" : "#fef2f2",
            borderRadius: "8px", padding: "8px 12px",
            border: `1px solid ${item.score >= item.max * 0.7 ? "#1A5C40" : item.score > 0 ? "#D4921A" : "#e5e7eb"}`,
          }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#333" }}>{item.label}</div>
            <div style={{ fontSize: "10px", color: "#666", marginTop: "2px" }}>{item.detail}</div>
            <div style={{ fontSize: "12px", fontWeight: 800, color: getScoreColor(Math.round(item.score / item.max * 100)), marginTop: "4px" }}>
              {item.score}/{item.max} pts
            </div>
          </div>
        ))}
      </div>

      {/* Tier 2 breakdown if available */}
      {tier2Breakdown && tier2Breakdown.length > 0 && (
        <div style={{ marginBottom: "14px", padding: "10px", background: "#f4f6ff", borderRadius: "8px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#2A3390", marginBottom: "6px" }}>Life Address Match</div>
          {tier2Breakdown.map((group, i) => (
            <div key={i} style={{ marginBottom: "4px" }}>
              <span style={{ fontSize: "11px", fontWeight: 600, textTransform: "capitalize" }}>{group.type}: </span>
              {group.addresses.map((a, j) => (
                <span key={j} style={{ fontSize: "11px", color: "#555", marginRight: "8px" }}>
                  {a.label} — {a.detail}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* CTA line */}
      <div style={{ fontSize: "12px", color: "#555", marginBottom: "10px", textAlign: "center", fontStyle: "italic" }}>
        See how close to a 100% match this home is — get your AI analysis below or run a full report on the AI tools site
      </div>

      {/* AI Analysis CTA — quick inline */}
      {!aiResult && (
        <div style={{
          background: "linear-gradient(135deg,#1A2280,#2A3390 60%,#1A5C40)",
          borderRadius: "10px", padding: "14px 18px",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px",
          marginBottom: "8px",
        }}>
          <div>
            <div style={{ color: "#D4921A", fontSize: "10px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              AI Personality Analysis — Free
            </div>
            <div style={{ color: "white", fontSize: "13px", fontWeight: 700 }}>
              See why this home matches your lifestyle, beyond the numbers
            </div>
          </div>
          <button
            onClick={onGetAIAnalysis}
            disabled={aiLoading}
            style={{
              background: aiLoading ? "#666" : "#D4921A", color: "#2A1600",
              border: "none", borderRadius: "50px", padding: "8px 20px",
              fontWeight: 800, fontSize: "12px", cursor: aiLoading ? "default" : "pointer",
              whiteSpace: "nowrap",
            }}>
            {aiLoading ? "Analyzing..." : "✨ Get Full AI Analysis"}
          </button>
        </div>
      )}

      {/* AI Site deep-link banner */}
      {!aiResult && (
        <div style={{
          background: "#f0f4ff", border: "1.5px solid #2A3390",
          borderRadius: "10px", padding: "12px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: "8px",
        }}>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 800, color: "#2A3390", marginBottom: "2px" }}>
              🏡 Full AI Home Report
            </div>
            <div style={{ fontSize: "11px", color: "#555" }}>
              Run a full AI analysis on this home — inspection insights, market value, health &amp; safety risks, and more
            </div>
          </div>
          <button
            onClick={function() {
              var addr = window._mhbCurrentAddress || document.title;
              var base = "https://ai.myhomebuddha.com?mode=buyer&address=" + encodeURIComponent(addr);
              try {
                var session = window.Amplify && window.Amplify.Auth
                  ? null : null;
                var token = localStorage.getItem("userToken") || "";
                if (token) base += "&token=" + encodeURIComponent(token);
              } catch(e) {}
              window.open(base, "_blank");
            }}
            style={{
              background: "#2A3390", color: "white",
              border: "none", borderRadius: "50px", padding: "8px 18px",
              fontWeight: 700, fontSize: "11px", cursor: "pointer",
              whiteSpace: "nowrap",
            }}>
            Open Full Report →
          </button>
        </div>
      )}

      {/* AI Result */}
      {aiResult && (
        <div style={{ background: "#f8faff", borderRadius: "10px", padding: "14px 18px", borderLeft: "3px solid #2A3390" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#2A3390", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            ✨ AI Personality Match Analysis
          </div>
          <div style={{ fontSize: "13px", color: "#333", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{aiResult}</div>
        </div>
      )}
    </div>
  );
}